import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { SubmitButton } from '../components/Buttons';
import { ArrayInput, Select, TextFormField } from '../components/FormFields';
import { GenerateCsv } from '../components/GenerateCsv';
import { GeneratePdf } from '../components/GeneratePdf';
import Message from '../components/Message';
import { StockageSelect } from '../components/StockageSelect';
import { Context } from '../components/Store';
import { getClients } from '../functions/client';
import { getFiche, getFiches, updateFiche } from '../functions/fiche';
import { deleteImage } from '../functions/images';
import { getStockages } from '../functions/stockage';
import { getUsers } from '../functions/user';
import { getVehicles } from '../functions/vehicle';
import { ficheFields } from '../utils/constants';
import {
  formatDate,
  formatMillisToTime,
  formatTimeToMillis,
  sectionTotale,
} from '../utils/utils';

/**
 * Fieldsets pour lesquels on affiche le schéma de la garniture mécanique
 * (fiches Démontage - Pompe = 21, Démontage - Motopompe = 27)
 */
const SCHEMA_POMPE_FIELDSETS = [21, 27];

/**
 * Libellés des champs qu'un technicien peut voir/modifier sur l'écran de révision,
 * même si tous les autres champs lui sont masqués.
 */
const TECHNICIEN_VISIBLE_LABELS = ['Statut'];

/**
 * Sanitize les valeurs des champs select d'une fiche :
 * - Si la value est valide → on la garde
 * - Si la value correspond à un ancien label stocké en base → on la remplace par la bonne value
 * - Si la value est totalement inconnue → on reset à ''
 */
const sanitizeFicheValues = (ficheData) => {
  const sanitized = { ...ficheData };

  Object.values(ficheFields).forEach(({ fields }) => {
    Object.entries(fields).forEach(([fieldKey, fieldDef]) => {
      if (fieldDef.type === 'select' && fieldDef.values && !fieldDef.multiple) {
        const val = sanitized[fieldKey];
        if (val !== undefined && val !== '' && val !== null) {
          const validValues = fieldDef.values.map((o) => String(o.value));
          if (!validValues.includes(String(val))) {
            const matchByLabel = fieldDef.values.find(
              (o) => o.label.toLowerCase() === String(val).toLowerCase()
            );
            if (matchByLabel) {
              console.warn(
                `[sanitize] "${fieldKey}": "${val}" → remappé vers ${matchByLabel.value}`
              );
              sanitized[fieldKey] = matchByLabel.value;
            } else {
              console.warn(
                `[sanitize] "${fieldKey}": valeur inconnue "${val}" → reset`
              );
              sanitized[fieldKey] = '';
            }
          }
        }
      }
    });
  });

  return sanitized;
};

/**
 * Calcule le label d'un champ en fonction du contexte triphasé/monophasé,
 * sans muter l'objet field d'origine (qui est partagé entre toutes les fiches).
 */
const resolveFieldLabel = (
  f,
  field,
  isTriphase,
  isRemontage,
  isMonophase,
  isIntensiteWHidden
) => {
  if (f === 'intensiteV' && isIntensiteWHidden) {
    return 'Intensité N';
  }

  if (isTriphase) {
    const triphaseLabels = {
      isolementPhaseMasseU: 'Isolement phase/masse U',
      isolementPhaseMasseV: 'Isolement phase/masse V',
      isolementPhaseMasseW: 'Isolement phase/masse W',
      isolementPhasePhaseU: 'Isolement phase/phase U',
      isolementPhasePhaseV: 'Isolement phase/phase V',
      isolementPhasePhaseW: 'Isolement phase/phase W',
      resistanceStatorU: 'Résistance stator U',
      resistanceStatorV: 'Résistance stator V',
      resistanceStatorW: 'Résistance stator W',
      tension: 'Tension',
      intensiteU: 'Intensité U',
      intensiteV: 'Intensité V',
      intensiteW: 'Intensité W',
      tensionV: 'Tension V',
    };
    if (triphaseLabels[f]) return triphaseLabels[f];
  } else {
    const monophaseLabels = {
      isolementPhaseMasseU: 'Isolement Phase-Masse',
      resistanceStatorU: 'Résistance Neutre-Démarrage',
      resistanceStatorV: 'Résistance Neutre-Travail',
      resistanceStatorW: 'Résistance Travail-Démarrage',
      intensiteU: 'Intensité U',
      intensiteV: 'Intensité V',
      tensionV: 'Tension N',
    };
    if (monophaseLabels[f]) return monophaseLabels[f];
  }

  return field.label;
};

export default function FicheForm(props) {
  const [state] = useContext(Context);
  const history = useHistory();
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');
  const [info, setInfo] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [fiche, setFiche] = useState();
  const [fetchedData, setFetchedData] = useState(null);
  const isTechnicien = state.user?.status === 1;
  const initialTypeBobinage = fiche?.subtype === 2 ? 'continu' : 'alternatif';

  /**
   * Vérifie si un champ doit être affiché en fonction de la condition showIf
   */
  const shouldShowField = (field, fiche) => {
    if (!field.showIf) return true;
    const { field: dependentField, value: expectedValue } = field.showIf;
    const currentValue = fiche[dependentField];
    if (typeof expectedValue === 'boolean') {
      if (currentValue === 'true') return expectedValue === true;
      if (currentValue === 'false') return expectedValue === false;
      return currentValue === expectedValue;
    }
    return currentValue === expectedValue;
  };

  const submitForm = async () => {
    if (fiche.numDevis === '') return setError('Le numéro de devis est requis');
    if (fiche.numFiche === '') return setError('Le numéro de fiche est requis');
    setError('');
    const data = {
      ...fiche,
      ...(fiche.dateDebut &&
        fiche.dateDebut !== '' && {
          dateDebut: `${fiche.dateDebut}T12:00:00.003Z`,
        }),
      ...(fiche.dureeTotale &&
        fiche.dureeTotale !== '' && {
          dureeTotale: formatTimeToMillis(fiche.dureeTotale),
        }),
    };
    const imagesToDelete =
      JSON.parse(localStorage.getItem('imagesToDelete')) || [];
    await Promise.all(
      imagesToDelete.map((image) => deleteImage(image.imageName, image.token))
    );
    localStorage.removeItem('imagesToDelete');
    if (data._id) delete data._id;
    if (data.author) delete data.author;
    // La zone de stockage affichée sur une fiche remontage ou bobinage vient de la
    // fiche démontage liée (même numDevis) : ce n'est pas un champ de ce document,
    // on ne le renvoie donc jamais dans le payload d'update pour ces types.
    if ([3, 4].includes(parseInt(data.type))) delete data.stockage;
    setIsLoading(true);
    const res = await updateFiche(props.match.params.id, data, state.token);
    setIsLoading(false);
    if (res.error) return setError(res.error);
    localStorage.setItem('message', `La fiche a bien été mise à jour.`);
    return history.push(
      `/?${new URLSearchParams(props.location.search).toString()}`
    );
  };

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    let fetchedVehicles = await getVehicles(state.token);
    let fetchedUsers = await getUsers(state.token, { status: [1, 2, 3, 4] });
    let fetchedClients = await getClients(state.token, { limit: 5000 });
    let fetchedStockages = await getStockages(state.token);
    setIsLoading(false);
    if (fetchedVehicles.error) return setError(fetchedVehicles.error);
    if (fetchedUsers.error) return setError(fetchedUsers.error);
    if (fetchedClients.error) return setError(fetchedClients.error);
    if (fetchedStockages.error) return setError(fetchedStockages.error);
    if (fetchedClients.data.length === 0)
      return setError("Veuillez d'abord créer un client");
    fetchedVehicles = [
      { value: '', label: 'Aucun véhicule sélectionné.' },
      ...fetchedVehicles.data.map((v) => {
        return { value: v._id, label: v.nom };
      }),
    ];
    fetchedUsers = [
      { value: '', label: 'Aucun responsable sélectionné.' },
      ...fetchedUsers.data.map((u) => {
        return {
          value: u._id,
          label: `${u.prenom || u.username} ${u.nom || ''}`,
        };
      }),
    ];
    fetchedClients = fetchedClients.data.map((c) => {
      return { value: c._id, label: c.enterprise };
    });
    fetchedStockages = [
      { value: '', label: 'Aucune zone sélectionnée.' },
      ...fetchedStockages.data.map((s) => {
        return { value: s._id, label: s.nom };
      }),
    ];
    return setFetchedData({
      clients: fetchedClients,
      users: fetchedUsers,
      vehicles: fetchedVehicles,
      stockages: fetchedStockages,
    });
  }, [state.token]);

  const fetchFiche = useCallback(async () => {
    setInfo('');
    setError('');
    setWarning('');
    setIsLoading(true);
    const fetchedFiche = await getFiche(state.token, props.match.params.id);
    setIsLoading(false);
    if (fetchedFiche.error) return setError(fetchedFiche.error);
    setFiche({
      ...sanitizeFicheValues(fetchedFiche.data),
      ...(fetchedFiche.data.client && { client: fetchedFiche.data.client._id }),
      ...(fetchedFiche.data.resp && { resp: fetchedFiche.data.resp._id }),
      ...(fetchedFiche.data.stockage && {
        stockage:
          typeof fetchedFiche.data.stockage === 'object'
            ? fetchedFiche.data.stockage._id
            : fetchedFiche.data.stockage,
      }),
      ...(fetchedFiche.data.author && {
        author: fetchedFiche.data.author.username,
      }),
      ...(fetchedFiche.data.techniciens && {
        techniciens: fetchedFiche.data.techniciens.map((u) => u),
      }),
      ...(fetchedFiche.data.dateDebut && {
        dateDebut: formatDate(fetchedFiche.data.dateDebut),
      }),
      ...{
        dureeTotale: formatMillisToTime(fetchedFiche.data?.dureeTotale || 0),
      },
    });
  }, [state.token, props.match.params.id]);

  useEffect(() => {
    fetchFiche();
    fetchData();
  }, [fetchFiche, fetchData]);

  /**
   * Pour une fiche remontage ou bobinage, la zone de stockage n'existe pas sur ce
   * document : on va la chercher sur la fiche démontage liée par le même numéro de devis.
   */
  const fetchLinkedStockage = useCallback(async () => {
    if (!fiche || ![3, 4].includes(parseInt(fiche.type)) || !fiche.numDevis)
      return;
    if (fiche.stockage) return; // déjà résolue, pas la peine de refaire l'appel
    const related = await getFiches(state.token, {
      filter: fiche.numDevis,
      types: [2],
    });
    if (related.error) return;
    const demontageFiche = (related.data || []).find(
      (f) => f.numDevis === fiche.numDevis
    );
    if (demontageFiche?.stockage) {
      setFiche((prev) => ({
        ...prev,
        stockage:
          typeof demontageFiche.stockage === 'object'
            ? demontageFiche.stockage._id
            : demontageFiche.stockage,
      }));
    }
  }, [state.token, fiche]);

  useEffect(() => {
    fetchLinkedStockage();
  }, [fiche?.type, fiche?.numDevis]);

  document.title = "Révision d'une fiche";

  useEffect(() => {
    if (fiche && fiche.typeBobinage === undefined) {
      setFiche((prev) => ({
        ...prev,
        typeBobinage: initialTypeBobinage,
      }));
    }
  }, [fiche, initialTypeBobinage]);

  return (
    <div className="flex flex-col items-center">
      <div className="w-full lg:w-11/12 mb-10">
        <div className="mt-6 text-2xl sm:text-3xl md:text-4xl uppercase font-bold mb-8" />
        {error ? <Message message={error} setError={setError} /> : null}
        {warning ? (
          <Message message={warning} type="warning" setError={setWarning} />
        ) : null}
        {info ? (
          <Message message={info} type="success" setError={setInfo} />
        ) : null}
        <div className="bg-viguierBleu rounded">
          <div className="bg-viguierBleu text-white rounded-t">
            <div className="flex items-center justify-between py-2 px-4">
              {fiche && fetchedData && !isTechnicien && (
                <div className="w-1/3">
                  <GenerateCsv fiche={fiche} fetchedData={fetchedData} />
                </div>
              )}
              <div className="flex w-1/3 justify-center">
                <h2 className="text-xs lg:text-2xl font-bold">
                  Révision d'une fiche
                </h2>
              </div>
              {fiche && fetchedData && !isTechnicien && (
                <div className="w-1/3">
                  <GeneratePdf
                    fiche={fiche}
                    fetchedData={fetchedData}
                    setError={setError}
                  />
                </div>
              )}
            </div>
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              submitForm();
            }}
            className="px-4 py-3 text-white font-bold"
          >
            {fiche &&
              fetchedData &&
              Object.keys(ficheFields).map((typeFiche) => {
                const showFieldset =
                  parseInt(typeFiche) === 0 ||
                  (parseInt(typeFiche) !== 4 &&
                    parseInt(typeFiche) !== 41 &&
                    parseInt(typeFiche) === parseInt(fiche.type)) ||
                  (parseInt(fiche.type) === 2 &&
                    parseInt(fiche.subtype) === parseInt(typeFiche) - 20) ||
                  (parseInt(fiche.type) === 3 &&
                    parseInt(fiche.subtype) === parseInt(typeFiche) - 30) ||
                  (parseInt(fiche.type) === 4 &&
                    parseInt(fiche.subtype) !== 2 &&
                    parseInt(typeFiche) === 4) ||
                  (parseInt(fiche.type) === 4 &&
                    parseInt(fiche.subtype) === 2 &&
                    parseInt(typeFiche) === 41) ||
                  (parseInt(typeFiche) === 42 && fiche.collecteur === true);

                return (
                  <fieldset
                    key={typeFiche}
                    className="border-2 border-white pt-2 mb-4 rounded bg-viguierBleuClair"
                    hidden={!showFieldset}
                  >
                    <legend className="px-3 ml-1d text-xl">
                      {ficheFields[typeFiche].title}
                    </legend>
                    {SCHEMA_POMPE_FIELDSETS.includes(parseInt(typeFiche)) && (
                      <div className="flex flex-col items-center px-4 pb-4">
                        <img
                          src="/schema-garniture-mecanique.png"
                          alt="Schéma de la garniture mécanique"
                          className="max-w-full sm:max-w-md bg-white rounded p-2"
                        />
                        <p className="text-xs font-normal mt-2 text-center max-w-md">
                          D1 : Diamètre de l'arbre — D3 : Diamètre extérieur de
                          la partie rotative — D7 : Diamètre extérieur de la
                          partie fixe — l3 : Longueur de la partie rotative en
                          position de travail — l4 : Epaisseur de la partie fixe
                        </p>
                      </div>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                      {Object.keys(ficheFields[typeFiche].fields).map(
                        (f, i) => {
                          const field = ficheFields[typeFiche].fields[f];
                          if (!shouldShowField(field, fiche)) return null;

                          const isTriphase =
                            parseInt(fiche?.typeMotopompe) === 1 ||
                            parseInt(fiche?.typeMotoreducteur) === 1 ||
                            (parseInt(fiche?.type) === 3 &&
                              parseInt(fiche?.subtype) === 6) ||
                            (parseInt(fiche?.type) === 2 &&
                              parseInt(fiche?.subtype) === 6);
                          const isMonophase =
                            parseInt(fiche?.typeMotopompe) === 2 ||
                            parseInt(fiche?.typeMotoreducteur) === 2 ||
                            (parseInt(fiche?.type) === 3 &&
                              parseInt(fiche?.subtype) === 2);
                          const isRemontage = parseInt(fiche.type) === 3;
                          const isDemontage = parseInt(fiche.type) === 2;

                          // === Masquage des champs spécifiques au démontage ===
                          if (isDemontage) {
                            const monophaseOnlyFields = [
                              'resistanceTravail',
                              'resistanceDemarrage',
                              'valeurCondensateur',
                            ];
                            const triphaseOnlyFields = [
                              'isolementPhaseMasseStatorUM',
                              'isolementPhaseMasseStatorVM',
                              'isolementPhaseMasseStatorWM',
                              'isolementPhasePhaseStatorUV',
                              'isolementPhasePhaseStatorVW',
                              'isolementPhasePhaseStatorUW',
                              'resistanceStatorU',
                              'resistanceStatorV',
                              'resistanceStatorW',
                              'tensionW',
                              'intensiteW',
                              'isolementPhaseMasseW',
                              'isolementPhasePhaseW',
                            ];
                            if (isTriphase && monophaseOnlyFields.includes(f))
                              return null;
                            if (!isTriphase && triphaseOnlyFields.includes(f))
                              return null;
                          } else if (isRemontage && isMonophase) {
                            const triphaseFieldsToHide = [
                              'isolementPhaseMasseV',
                              'isolementPhaseMasseW',
                              'isolementPhasePhaseU',
                              'isolementPhasePhaseV',
                              'isolementPhasePhaseW',
                              'tension',
                              'tensionW',
                              'intensiteW',
                            ];
                            if (triphaseFieldsToHide.includes(f)) return null;
                          }

                          // Masquage uniquement pour les vrais monophasés (subtype=2)
                          // Ne pas masquer pour courant continu (subtype=5), alternateur (subtype=3), etc.
                          const isStrictMonophase =
                            parseInt(fiche?.typeMotopompe) === 2 ||
                            parseInt(fiche?.typeMotoreducteur) === 2 ||
                            parseInt(fiche?.subtype) === 2;

                          const isIntensiteWHidden =
                            (isDemontage && !isTriphase) ||
                            (isRemontage && isMonophase) ||
                            (isStrictMonophase && !isTriphase);
                          if (
                            isStrictMonophase &&
                            !isTriphase &&
                            ['tension', 'intensiteW'].includes(f)
                          )
                            return null;

                          // === Résolution du label sans mutation ===
                          const fieldLabel = resolveFieldLabel(
                            f,
                            field,
                            isTriphase,
                            isRemontage,
                            isMonophase,
                            isIntensiteWHidden
                          );

                          if (
                            isTechnicien &&
                            !TECHNICIEN_VISIBLE_LABELS.includes(fieldLabel)
                          )
                            return null;

                          // === Rendu des champs ===
                          switch (field.type) {
                            case 'select':
                              if (field.data === 'stockages') {
                                return (
                                  <StockageSelect
                                    value={fiche[f]}
                                    setter={(newVal) =>
                                      setFiche((prev) => ({
                                        ...prev,
                                        [f]: newVal,
                                      }))
                                    }
                                    label={fieldLabel}
                                    id={f}
                                    key={i}
                                    required={field.required}
                                    readonly={field.readonly}
                                    options={fetchedData[field.data]}
                                  />
                                );
                              }
                              return (
                                <Select
                                  value={
                                    field.multiple
                                      ? fiche[f]
                                        ? fiche[f].map((a) => a.value || a)
                                        : []
                                      : fiche[f] || initialTypeBobinage
                                  }
                                  setter={(newVal) => {
                                    setFiche((prev) => {
                                      if (f === 'typeBobinage') {
                                        const newSubtype =
                                          newVal === 'continu' ? 2 : 1;
                                        return {
                                          ...prev,
                                          [f]: newVal,
                                          subtype: newSubtype,
                                          ...(newSubtype !== 2 && {
                                            collecteur: false,
                                          }),
                                        };
                                      }
                                      return { ...prev, [f]: newVal };
                                    });
                                  }}
                                  label={fieldLabel}
                                  id={f}
                                  key={i}
                                  required={field.required}
                                  multiple={field.multiple}
                                  readonly={field.readonly}
                                  options={
                                    field.values
                                      ? field.required
                                        ? field.values
                                        : [
                                            {
                                              value: '',
                                              label: 'Sélectionner...',
                                            },
                                            ...field.values,
                                          ]
                                      : field.multiple &&
                                        fetchedData &&
                                        fetchedData[field.data]?.length > 0
                                      ? fetchedData[field.data].slice(1)
                                      : fetchedData[field.data]
                                  }
                                />
                              );
                            case 'array':
                              return (
                                <ArrayInput
                                  values={fiche[f] || []}
                                  setter={(newVal) =>
                                    setFiche((prev) => ({
                                      ...prev,
                                      [f]: newVal,
                                    }))
                                  }
                                  label={
                                    f === 'sectionsFils' ? (
                                      <span className="font-thin">
                                        {fieldLabel}
                                        <i>
                                          {` - Section totale : `}
                                          <span className="font-extrabold">{`${sectionTotale(
                                            fiche[f] || []
                                          )}`}</span>
                                          mm²
                                        </i>
                                      </span>
                                    ) : (
                                      fieldLabel
                                    )
                                  }
                                  format={
                                    field?.subtype === 'object' && field.values
                                  }
                                  options={
                                    field?.subtype === 'select' && field.values
                                  }
                                  id={f}
                                  key={i}
                                  type={field?.subtype}
                                  numFiche={fiche.numFiche}
                                  setError={setError}
                                  setInfo={setInfo}
                                />
                              );
                            default:
                              return (
                                <TextFormField
                                  type={field.type || 'text'}
                                  value={fiche[f] ?? ''}
                                  setter={(newVal) =>
                                    setFiche((prev) => ({
                                      ...prev,
                                      [f]: newVal,
                                    }))
                                  }
                                  unit={field.unit}
                                  id={f}
                                  key={i}
                                  required={field.required}
                                  readonly={field.readonly}
                                  options={field.options || []}
                                  numFiche={fiche.numFiche}
                                  setError={setError}
                                  setInfo={setInfo}
                                  {...field}
                                  label={fieldLabel}
                                />
                              );
                          }
                        }
                      )}
                    </div>
                  </fieldset>
                );
              })}
          </form>
          <div className="bg-viguierBleu text-white items-center justify-between flex rounded-b px-4 py-2">
            <div />
            {fiche && (
              <SubmitButton
                action={submitForm}
                label={`${
                  isLoading
                    ? 'Chargement...'
                    : 'Confirmer la révision de la fiche'
                }`}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
